import { TabHorizontalSafeAreaContext } from '@/lib/hooks/useHorizontalSafeAreaPadding'
import { isBelowiOS26 } from '@/lib/utils/iosCompat'
import { Stack, type NativeStackNavigationOptions } from 'expo-router'
import { useTheme } from 'expo-router/react-navigation'
import type { PropsWithChildren } from 'react'
import { Platform } from 'react-native'
import {
  SafeAreaFrameContext,
  SafeAreaInsetsContext,
  SafeAreaProvider,
  useSafeAreaFrame,
  useSafeAreaInsets,
  type EdgeInsets,
  type Rect,
} from 'react-native-safe-area-context'

export default function TabStackLayout({ title }: { title: string }) {
  const theme = useTheme()
  const windowInsets = useSafeAreaInsets()
  const windowFrame = useSafeAreaFrame()

  return (
    // The iPadOS 26 sidebar floats over the tab content and only adds a left inset to it, not to the window, so
    // measure the tab content itself and let the lists pad their content beside the sidebar
    <SafeAreaProvider
      initialMetrics={{ insets: windowInsets, frame: windowFrame }}
      style={{ flex: 1, backgroundColor: theme.colors.background }}
    >
      <TabSafeArea windowInsets={windowInsets} windowFrame={windowFrame}>
        <Stack
          screenOptions={{
            title: '',
            headerLargeTitle: true,
            headerShadowVisible: false,
            headerBackButtonDisplayMode: 'minimal',
            contentStyle: { backgroundColor: theme.colors.background },
            ...Platform.select<NativeStackNavigationOptions>({
              ios: {
                // Opaque header on iOS <26 desyncs RN touch/measure coordinates from the
                // visual layout after push+pop (taps land ~header-height off and get
                // suppressed by Pressability), so keep the header transparent and use a
                // blur effect for the pre-26 look.
                headerTransparent: true,
                ...(isBelowiOS26 ? { headerBlurEffect: 'systemChromeMaterial' as const } : {}),
              },
              android: {
                headerTransparent: false,
                headerStyle: { backgroundColor: theme.colors.background },
              },
            }),
          }}
        >
          <Stack.Screen name="index" options={{ title }} />
        </Stack>
      </TabSafeArea>
    </SafeAreaProvider>
  )
}

/**
 * Hands the tab's horizontal insets to the lists, and restores the window's metrics for everything else: the tab
 * content's own top and bottom insets include the bars, which the screens already lay out around.
 */
function TabSafeArea({
  windowInsets,
  windowFrame,
  children,
}: PropsWithChildren<{ windowInsets: EdgeInsets; windowFrame: Rect }>) {
  const { left, right } = useSafeAreaInsets()

  return (
    <TabHorizontalSafeAreaContext value={{ left, right }}>
      <SafeAreaInsetsContext value={windowInsets}>
        <SafeAreaFrameContext value={windowFrame}>{children}</SafeAreaFrameContext>
      </SafeAreaInsetsContext>
    </TabHorizontalSafeAreaContext>
  )
}
